/**
 * KPC Floating Cart Button — mobile PDP quick add-to-cart.
 * Adds the currently selected variant + qty 1 to cart via /cart/add.js.
 * Plays success animation, refreshes the cart count, and emits the same
 * analytics events as the standard product form so attribution stays clean.
 */
(function () {
  'use strict';

  const SELECTOR_BUTTON = '.kpc-fc';
  const SELECTOR_FORM = 'form[action$="/cart/add"]';

  function getSelectedVariantId(form) {
    const idInput = form.querySelector('input[name="id"]');
    if (!idInput || !idInput.value) return null;
    return idInput.value;
  }

  function setLoading(btn, loading) {
    btn.classList.toggle('is-loading', loading);
    btn.disabled = loading;
    if (loading) {
      btn.dataset.originalHtml = btn.innerHTML;
      btn.innerHTML = '<span class="kpc-fc__spinner" aria-hidden="true"></span>';
    } else if (btn.dataset.originalHtml) {
      btn.innerHTML = btn.dataset.originalHtml;
      delete btn.dataset.originalHtml;
    }
  }

  function emitCartUpdate(response) {
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: response }));

    if (!response || !response.sections) return;

    const cart = document.querySelector('cart-drawer, cart-notification');
    if (cart && typeof cart.renderContents === 'function') {
      cart.renderContents(response);
    }

    // Mirror Dawn's CartItems.updateQuantity behavior: keep is-empty
    // in sync on the cart-drawer host element and main-cart-footer.
    // The built-in CartDrawer.renderContents only swaps inner HTML and
    // never touches these, so the first item added from an empty cart
    // leaves is-empty on the host and the drawer renders with the
    // empty-state layout (hidden header, grid drawer__inner) even
    // though items exist.
    //
    // /cart/add.js does not include item_count in its top-level
    // response, but a successful add means the cart cannot be empty,
    // so the unconditional remove is safe here.
    document.querySelectorAll('cart-drawer').forEach((el) => {
      el.classList.remove('is-empty');
    });
    const cartFooter = document.getElementById('main-cart-footer');
    if (cartFooter) cartFooter.classList.remove('is-empty');

    // Refresh the cart-icon-bubble. The stock CartDrawer.renderContents
    // loop does getElementById('cart-icon-bubble').innerHTML = ..., but
    // that id belongs to the inner <a>, so the assignment nests anchors
    // and leaves the visible count stale. In the current theme the
    // bubble lives inside the header section group, not its own section
    // wrapper, so the cleanest fix is to swap the <a> element itself.
    const bubbleHtml = response.sections && response.sections['cart-icon-bubble'];
    if (bubbleHtml) {
      const parsed = new DOMParser().parseFromString(bubbleHtml, 'text/html');
      const newAnchor =
        parsed.getElementById('cart-icon-bubble') ||
        parsed.querySelector('a[href$="/cart"]');
      const oldAnchor = document.getElementById('cart-icon-bubble');
      if (newAnchor && oldAnchor) {
        oldAnchor.replaceWith(newAnchor);
      }
    }
  }

  function trackAddToCart(variantId, productHandle) {
    if (window.posthog && typeof window.posthog.capture === 'function') {
      window.posthog.capture('add_to_cart', {
        variant_id: variantId,
        product_handle: productHandle,
        source: 'floating_button',
      });
    }
  }

  function showSuccess(btn) {
    if (btn.classList.contains('is-success')) return;
    btn.classList.add('is-success');
    setTimeout(() => btn.classList.remove('is-success'), 600);
  }

  function showShake(btn) {
    if (btn.classList.contains('is-shaking')) return;
    btn.classList.add('is-shaking');
    if (navigator.vibrate) navigator.vibrate(12);
    setTimeout(() => btn.classList.remove('is-shaking'), 420);
  }

  function showErrorToast(message) {
    if (!message) return;
    const existing = document.querySelector('.kpc-fc-toast');
    if (existing) existing.remove();
    const toast = document.createElement('div');
    toast.className = 'kpc-fc-toast';
    toast.setAttribute('role', 'alert');
    toast.textContent = message;
    document.body.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    setTimeout(() => {
      toast.classList.remove('is-visible');
      setTimeout(() => toast.remove(), 250);
    }, 2400);
  }

  async function addToCart(btn) {
    const form = document.querySelector(SELECTOR_FORM);
    if (!form) return;

    const variantId = getSelectedVariantId(form);
    if (!variantId) {
      showShake(btn);
      return;
    }

    if (btn.classList.contains('is-unavailable')) {
      showShake(btn);
      return;
    }

    setLoading(btn, true);

    try {
      const fd = new FormData();
      fd.append('id', variantId);
      fd.append('quantity', '1');

      const cart = document.querySelector('cart-drawer, cart-notification');
      if (cart && typeof cart.getSectionsToRender === 'function') {
        try {
          const sectionIds = cart.getSectionsToRender().map((s) => s.id || s.section).filter(Boolean);
          if (sectionIds.length) {
            fd.append('sections', sectionIds.join(','));
            fd.append('sections_url', window.location.pathname);
          }
        } catch (e) {}
      }

      const addUrl = (window.routes && window.routes.cart_add_url) ||
        `${window.Shopify.routes.root}cart/add.js`;

      const response = await fetch(addUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: fd,
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.description || `HTTP ${response.status}`);
      }

      const handle = btn.dataset.productHandle || '';
      trackAddToCart(variantId, handle);
      emitCartUpdate(data);
      setLoading(btn, false);
      showSuccess(btn);
    } catch (err) {
      console.warn('[kpc-floating-cart] add failed', err);
      setLoading(btn, false);
      showShake(btn);
      showErrorToast(err && err.message ? err.message : 'Could not add to cart');
    }
  }

  function syncAvailability(btn) {
    const form = document.querySelector(SELECTOR_FORM);
    if (!form) return;
    const submit = form.querySelector('[type="submit"]');
    const isSoldOut = submit ? submit.disabled : false;
    btn.classList.toggle('is-unavailable', isSoldOut);
  }

  function init() {
    const btn = document.querySelector(SELECTOR_BUTTON);
    if (!btn) return;

    // NOTE: relocateToMainGallery removed. Cart FAB is now position:fixed
    // and stays in its original DOM position inside .product__media-wrapper.
    // data-gallery-target attribute is kept as dead data for easy rollback.

    btn.addEventListener('click', (e) => {
      e.preventDefault();
      if (btn.classList.contains('is-loading')) return;
      addToCart(btn);
    });

    syncAvailability(btn);

    const form = document.querySelector(SELECTOR_FORM);
    if (form) {
      const submit = form.querySelector('[type="submit"]');
      if (submit) {
        new MutationObserver(() => syncAvailability(btn))
          .observe(submit, { attributes: true, attributeFilter: ['disabled'] });
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
